import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, getDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase.js';
import { getDisplayMeasurement } from '../utils/unitConversion.js';

export const useRecipeDetail = ({ 
  recipeId, 
  user, 
  recipes, 
  inventory, 
  scrollRef, 
  onRecipeDeleted,
  defaultMeasurementSystem = 'imperial'
}) => {
  const navigate = useNavigate();
  const [isMenuDialogOpen, setIsMenuDialogOpen] = useState(false);
  const [isCreateMenuDialogOpen, setIsCreateMenuDialogOpen] = useState(false);
  const [newMenuName, setNewMenuName] = useState('');
  const [imageError, setImageError] = useState(false);
  const [userRecipe, setUserRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  const [measurementSystem, setMeasurementSystem] = useState(defaultMeasurementSystem);
  const [servings, setServings] = useState(1);

  useEffect(() => {
    setMeasurementSystem(defaultMeasurementSystem);
  }, [defaultMeasurementSystem]);

  useEffect(() => {
    const fetchUserRecipe = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const recipeRef = doc(db, `users/${user.uid}/recipes/${recipeId}`);
        const recipeDoc = await getDoc(recipeRef);
        if (recipeDoc.exists()) {
          setUserRecipe({ id: recipeDoc.id, ...recipeDoc.data(), origin: 'My Recipes' });
        }
      } catch (error) {
        console.error('Error fetching user recipe:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserRecipe();
  }, [user, recipeId]);

  useEffect(() => {
    if (scrollRef && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
    const scrollContainers = document.querySelectorAll('[style*="overflow"]');
    scrollContainers.forEach(container => {
      if (container.scrollTop > 0) {
        container.scrollTop = 0;
      }
    });
  }, [scrollRef, recipeId]);

  const getGarnishArray = (recipe) => {
    if (Array.isArray(recipe.garnishes)) {
      return recipe.garnishes;
    }
    if (recipe.garnish) {
      const parts = recipe.garnish.split(/\s+and\s+/);
      if (parts.length > 1) {
        const lastItem = parts.pop();
        const otherItems = parts[0].split(/\s*,\s*/);
        return [...otherItems, lastItem].map(name => ({ name: name.trim() }));
      }
      return recipe.garnish.split(/\s*,\s*/).map(name => ({ name: name.trim() }));
    }
    return [];
  };

  const handleCreateAndAddToMenu = (onCreateMenu) => {
    if (newMenuName.trim()) {
      onCreateMenu({
        name: newMenuName.trim(),
        recipes: [recipeId]
      });
      setNewMenuName('');
      setIsCreateMenuDialogOpen(false);
      setIsMenuDialogOpen(false);
    }
  };

  const handleKeyPress = (event, onCreateMenu) => {
    if (event.key === 'Enter') {
      handleCreateAndAddToMenu(onCreateMenu);
    }
  };

  const cleanupUnusedIngredients = async () => {
    if (!user) return;
    
    try {
      const recipesRef = collection(db, `users/${user.uid}/recipes`);
      const querySnapshot = await getDocs(recipesRef);
      const userRecipes = querySnapshot.docs.map(doc => doc.data());
      
      const usedIngredients = new Set();
      userRecipes.forEach(recipe => {
        (recipe.ingredients || []).forEach(ing => {
          if (ing.ingredientId) {
            usedIngredients.add(ing.ingredientId);
          }
        });
      });
      
      const userIngredientsRef = collection(db, `users/${user.uid}/ingredients`);
      const userIngredientsSnapshot = await getDocs(userIngredientsRef);
      const userIngredients = userIngredientsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      const unusedCustomIngredients = userIngredients.filter(ing => 
        !usedIngredients.has(ing.id)
      );
      
      for (const ingredient of unusedCustomIngredients) {
        await deleteDoc(doc(db, `users/${user.uid}/ingredients`, ingredient.id));
        
        const inventoryRef = doc(db, `users/${user.uid}/inventory`, ingredient.id);
        const inventoryDoc = await getDoc(inventoryRef);
        
        if (inventoryDoc.exists()) {
          await deleteDoc(inventoryRef);
        }
      }
    } catch (error) {
      console.error('Error cleaning up unused ingredients:', error);
    }
  };

  const handleDeleteRecipe = async () => {
    if (!user || !userRecipe) return;
    
    try {
      const recipeRef = doc(db, `users/${user.uid}/recipes/${recipeId}`);
      await deleteDoc(recipeRef);
      setDeleteDialogOpen(false);
      
      await cleanupUnusedIngredients();
      
      if (onRecipeDeleted) {
        onRecipeDeleted(recipeId);
      }
      
      navigate('/recipes/my-recipes');
    } catch (error) {
      console.error('Error deleting recipe:', error);
    }
  };

  const recipe = userRecipe || recipes.find(r => r.id === recipeId);
  
  let processedRecipe = null;
  if (recipe) {
    const garnishArray = getGarnishArray(recipe);
    const hasEnhancedMeasurements = recipe.schemaVersion === 2 || 
      (recipe.ingredients && recipe.ingredients.some(ing => ing.measurement));
      
    const ingredientsWithPossession = (recipe.ingredients || []).map(ing => {
      const inventoryItem = inventory.find(i => i.id === ing.ingredientId);
      
      let displayAmount = ing.amount || '';
      
      if (hasEnhancedMeasurements) {
        if (ing.measurement) {
          displayAmount = getDisplayMeasurement(ing.measurement, measurementSystem, servings);
        } else if (ing.amount) {
          displayAmount = servings !== 1 ? `${ing.amount} × ${servings}` : ing.amount;
        }
      } else if (servings !== 1) {
        displayAmount = `${ing.amount} × ${servings}`;
      }
      
      const ingredientName = ing.name || 
                            (inventoryItem ? inventoryItem.name : null) || 
                            (ing.ingredientId && ing.ingredientId.includes('-') ? 
                              ing.ingredientId.split('-')
                                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                .join(' ') : 
                              ing.ingredientId);
      
      return {
        ...ing,
        name: ingredientName,
        ingredientId: ing.ingredientId || 'unknown',
        displayAmount: displayAmount,
        originalAmount: ing.amount,
        possessed: inventory.some(i => i.id === ing.ingredientId && i.possessed) || false
      };
    });
    
    const missing = ingredientsWithPossession.filter(ing => !ing.possessed);
    const missingCount = missing.length;

    const nameCounts = {};
    recipes.forEach(r => {
      nameCounts[r.name] = (nameCounts[r.name] || 0) + 1;
    });
    
    const recipeNameDisplay = nameCounts[recipe.name] > 1 ? `${recipe.name} (${recipe.origin})` : recipe.name;
    
    processedRecipe = {
      ...recipe,
      recipeNameDisplay,
      description: recipe.description || 'No description available',
      instructions: recipe.instructions || 'No instructions available',
      origin: recipe.origin || 'Unknown',
      ingredientsWithPossession,
      garnishArray,
      hasEnhancedMeasurements,
      missingCount,
      showImage: recipe.image && !recipe.image.includes("example.com") && !imageError
    };
  }

  return {
    loading,
    recipe: processedRecipe,
    servings,
    setServings,
    measurementSystem,
    setMeasurementSystem,
    imageError,
    setImageError,
    isMenuDialogOpen,
    setIsMenuDialogOpen,
    isCreateMenuDialogOpen,
    setIsCreateMenuDialogOpen,
    newMenuName,
    setNewMenuName,
    deleteDialogOpen,
    setDeleteDialogOpen,
    handleCreateAndAddToMenu,
    handleKeyPress,
    handleDeleteRecipe,
    userRecipe
  };
}; 